import { useEffect, useState } from 'react';
import axios from 'axios';

import { useBuyerReassignmentPopup } from 'features/checkout/reassignment/useBuyerReassignmentPopup';
import { useAuth } from 'providers/AuthProvider';
import { authService } from 'services/auth/authService';
import { orderService } from 'services/order/orderService';

import { OnSiteInitData, OnSiteParams } from './onsite.types';
import { useOnSiteAccountCheck } from './useOnSiteAccountCheck';
import { useOnSiteParams } from './useOnSiteParams';

const AUTH_TOKEN_TTL_IN_SECS = 60 * 60;

export const useOnSiteInitData = () => {
  const inputData = useOnSiteParams();
  const { showBuyerReassignmentPopup } = useBuyerReassignmentPopup();
  const { logged, appLogin } = useAuth();
  const { showAccountCheck } = useOnSiteAccountCheck();

  const [state, setState] = useState<OnSiteInitData>({ status: 'NOT_INITIALIZED' });
  const [guestMode, setGuestMode] = useState(false);

  useEffect(() => {
    if (state.status === 'NOT_INITIALIZED') {
      initData();
    } else if (state.status === 'LOGIN_REQUEST') {
      showAccountCheck({
        onContinueAsGuest: () => {
          setGuestMode(true);
          setState({ status: 'NOT_INITIALIZED' });
        },
        onLogin: () => {
          setGuestMode(false);
          setState({ status: 'NOT_INITIALIZED' });
        },
      });
    } else if (state.status === 'REASSIGNMENT_NEEDED') {
      showBuyerReassignmentPopup({
        purchase: state.purchase!!,
        onConfirmed: reassignBuyer,
      });
    }
  }, [state]);

  const initData = () => {
    if (inputData) {
      fetchInitialState(inputData)
        .then(state => setState(state))
        .catch(() => setState({ ...state, status: 'UNKNOWN_ERROR' }));
    } else {
      setState({ status: 'UNKNOWN_ERROR' });
    }
  };

  const reassignBuyer = () => {
    if (inputData) {
      const { orderId, reassignmentToken } = inputData;

      orderService
        .reassignBuyer(orderId, reassignmentToken!!)
        .then(() => setState(state => ({ ...state, status: 'INITIALIZED' })))
        .catch(() => setState({ ...state, status: 'UNKNOWN_ERROR' }));
    } else {
      setState({ status: 'UNKNOWN_ERROR' });
    }
  };

  const fetchInitialState = async (inputData: OnSiteParams): Promise<OnSiteInitData> => {
    const { orderId, username, password } = inputData;

    if (!logged && !guestMode) {
      return { status: 'LOGIN_REQUEST' };
    }

    if (logged) {
      const purchase = await fetchPurchase(orderId);
      if (purchase) {
        return { status: 'INITIALIZED', purchase };
      }
    }

    const { authToken } = await loginTechnicalUser(username, password);
    const purchase = await fetchPurchase(orderId, authToken);
    if (purchase) {
      if (logged) {
        return { status: 'REASSIGNMENT_NEEDED', purchase };
      } else {
        appLogin(authToken, 'Email');
        return { status: 'INITIALIZED', purchase };
      }
    } else {
      return { status: 'PURCHASE_NOT_FOUND' };
    }
  };

  const loginTechnicalUser = async (username: string, password: string) => {
    return (await authService.login(username, password, AUTH_TOKEN_TTL_IN_SECS)).data;
  };

  const fetchPurchase = async (orderId: number, authToken?: string) => {
    try {
      return (await orderService.fetchPurchaseDetails(orderId, authToken)).data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return null;
      }
      throw error;
    }
  };

  return { ...state };
};
